import { getBrandDefaultCurrency, getBrandPriceTierThresholds } from '../../shared/utils/brandCurrency';
import { CRM_PIPELINE_STAGES } from '../../shared/utils/crmConstants';

const brandTiers = getBrandPriceTierThresholds();
const TERMINAL_PIPELINE_STAGES = new Set(['closed_won', 'closed_lost']);

/** Canonical CRM module defaults — aligned with backend /crm/config route. */
export const DEFAULT_CRM_CONFIG = {
  leadScoring: true,
  autoAssignLeads: false,
  leadRotation: 'round_robin',
  maxLeadsPerUser: 50,
  defaultPipeline: 'sales',
  pipelineStages: 5,
  requireWinLossReason: true,
  autoMoveStages: false,
  monthlyGoalTracking: true,
  teamGoals: true,
  individualGoals: true,
  goalCurrency: getBrandDefaultCurrency(),
  defaultCountry: 'SA',
  defaultPhonePrefix: '+966',
  vipCustomerThreshold: 100000,
  highValueLeadThreshold: 50000,
  premiumCustomerThreshold: 50000,
  standardCustomerThreshold: brandTiers.lowMax,
  enableCampaigns: true,
  trackCampaignROI: true,
  autoSegmentation: false,
  emailIntegration: true,
  smsIntegration: false,
  callTracking: true,
  activityReminders: true,
  notifyNewLead: true,
  notifyDealWon: true,
  notifyGoalAchieved: true,
  notifyFollowUpDue: true,
  emailNotifications: true,
  dataEncryption: true,
  restrictClientData: true,
  auditTrail: true,
  defaultView: 'pipeline',
  showRevenue: true,
  itemsPerPage: 50,
  leadReferencePrefix: 'LED',
  autoGenerateLeadReference: true,
  contactReferencePrefix: 'CON',
  autoGenerateContactReference: true,
  opportunityReferencePrefix: 'OPP',
  autoGenerateOpportunityReference: true,
  campaignReferencePrefix: 'CMP',
  autoGenerateCampaignReference: true,
  interactionReferencePrefix: 'INT',
  autoGenerateInteractionReference: true,
  salesGoalReferencePrefix: 'GOL',
  autoGenerateSalesGoalReference: true,
};

export const CRM_ENTITY_PREFIX_MAP = {
  lead: 'leadReferencePrefix',
  contact: 'contactReferencePrefix',
  opportunity: 'opportunityReferencePrefix',
  campaign: 'campaignReferencePrefix',
  interaction: 'interactionReferencePrefix',
  salesGoal: 'salesGoalReferencePrefix',
};

const CRM_AUTO_REF_MAP = {
  lead: 'autoGenerateLeadReference',
  contact: 'autoGenerateContactReference',
  opportunity: 'autoGenerateOpportunityReference',
  campaign: 'autoGenerateCampaignReference',
  interaction: 'autoGenerateInteractionReference',
  salesGoal: 'autoGenerateSalesGoalReference',
};

const NESTED_PREFIX_TO_FLAT = {
  lead: 'leadReferencePrefix',
  contact: 'contactReferencePrefix',
  opportunity: 'opportunityReferencePrefix',
  campaign: 'campaignReferencePrefix',
  interaction: 'interactionReferencePrefix',
  salesGoal: 'salesGoalReferencePrefix',
};

/** Unwrap API payload and merge with defaults. */
export function normalizeCrmConfig(raw = {}) {
  let source = raw;
  if (source && typeof source === 'object' && source.data && typeof source.data === 'object') {
    source = source.data;
  }
  source = source && typeof source === 'object' ? { ...source } : {};
  const merged = { ...DEFAULT_CRM_CONFIG };

  Object.keys(DEFAULT_CRM_CONFIG).forEach((key) => {
    if (source[key] !== undefined && source[key] !== null) {
      merged[key] = source[key];
    }
  });

  if (source.entityPrefixes && typeof source.entityPrefixes === 'object') {
    Object.entries(NESTED_PREFIX_TO_FLAT).forEach(([entityKey, flatKey]) => {
      const nested = source.entityPrefixes[entityKey];
      if (nested && !source[flatKey]) {
        merged[flatKey] = nested;
      }
    });
  }

  merged.itemsPerPage = Math.max(1, parseInt(merged.itemsPerPage, 10) || DEFAULT_CRM_CONFIG.itemsPerPage);
  merged.pipelineStages = Math.max(1, Math.min(20, parseInt(merged.pipelineStages, 10) || DEFAULT_CRM_CONFIG.pipelineStages));
  merged.maxLeadsPerUser = Math.max(1, parseInt(merged.maxLeadsPerUser, 10) || DEFAULT_CRM_CONFIG.maxLeadsPerUser);

  delete merged.success;
  delete merged.entityPrefixes;
  return merged;
}

/** CRM list viewMode from config defaultView (pipeline | list | dashboard). */
export function resolveCrmListViewMode(defaultView) {
  if (defaultView === 'dashboard') return 'cards';
  return 'table';
}

/** Price tier thresholds from CRM config, fallback to brand defaults. */
export function getCrmPriceTierThresholds(crmConfig = {}) {
  const c = normalizeCrmConfig(crmConfig);
  const brand = getBrandPriceTierThresholds();
  return {
    highMin: parseFloat(c.vipCustomerThreshold) || brand.highMin,
    lowMax: parseFloat(c.standardCustomerThreshold) || brand.lowMax,
  };
}

export function getCrmEntityPrefix(settings, entityKey, fallback) {
  const configKey = CRM_ENTITY_PREFIX_MAP[entityKey];
  const fromCrm = configKey ? settings?.crm?.[configKey] : null;
  if (fromCrm) return String(fromCrm).trim() || fallback;
  return settings?.configuration?.entityPrefixes?.[entityKey] || fallback;
}

export function isCrmAutoReferenceEnabled(settings, entityKey) {
  const key = CRM_AUTO_REF_MAP[entityKey];
  if (!key) return true;
  const val = settings?.crm?.[key];
  return val !== false;
}

/** Map CRM config prefixes into settings.configuration.entityPrefixes. */
export function buildCrmEntityPrefixes(config) {
  const c = normalizeCrmConfig(config);
  return {
    lead: c.leadReferencePrefix,
    contact: c.contactReferencePrefix,
    opportunity: c.opportunityReferencePrefix,
    campaign: c.campaignReferencePrefix,
    interaction: c.interactionReferencePrefix,
    salesGoal: c.salesGoalReferencePrefix,
  };
}

/** Pipeline stages from CRM config — open stages capped by pipelineStages, terminal stages always included. */
export function getCrmPipelineStages(config = {}) {
  const normalized = normalizeCrmConfig(config);
  const count = Math.max(1, Math.min(20, parseInt(normalized.pipelineStages, 10) || CRM_PIPELINE_STAGES.length));
  const open = CRM_PIPELINE_STAGES.filter((s) => !TERMINAL_PIPELINE_STAGES.has(s.value));
  const terminal = CRM_PIPELINE_STAGES.filter((s) => TERMINAL_PIPELINE_STAGES.has(s.value));
  const openCount = Math.max(1, count - terminal.length);
  return [...open.slice(0, openCount), ...terminal];
}

/** Value tier label using CRM config thresholds (SAR). */
export function getCrmValueTierLabel(amount, config = {}, labels = {}) {
  const c = normalizeCrmConfig(config);
  const n = parseFloat(amount) || 0;
  if (n >= c.vipCustomerThreshold) return labels.high || 'VIP';
  if (n >= c.highValueLeadThreshold) return labels.high || 'عالي';
  if (n >= c.standardCustomerThreshold) return labels.medium || 'متوسط';
  return labels.low || 'منخفض';
}
